/* eslint-disable max-lines-per-function */
import React, { useState } from 'react';
import clsx from 'clsx';
import SearchTable from 'components/Organisms/SearchTable';
import VOPConfirmationModal from 'components/Payments/VOP/VOPConfirmationModal/VOPConfirmationModal';
import VOPStatusBadge from 'components/Payments/VOP/VOPStatusBadge';
import VOPCheckTooltip from 'components/VOPCheckTooltip/VOPCheckTooltip';
import useAuthorization from 'hooks/useAuthorization';
import { TSearchBaseResponse, TSearchParams } from 'hooks/useSearch';
import { UserRole } from 'models/user';
import { useSelector } from 'react-redux';
import { ITableColumn, ITableData } from 'services/DatabaseServices/marker.interface';
import browserHistory from 'services/history/browserHistory';
import { TStore } from 'store';
import { formatNumber } from 'utils/currency.helpers';
import formatIsoDate from 'utils/formatIsoDate';
import mapPaymentOrigin from 'utils/payments/mapPaymentOrigin';
import mapPaymentStatusDisplay from 'utils/payments/mapPaymentStatusDisplay';
import { shouldShowApprovalButtons } from 'utils/payments/shouldShowApprovalButtons';
import t from 'utils/translationHelper';

import { Approver } from '@alpha/auth-dtos';
import { PaymentDto, PaymentStatus, PaymentVopMatchStatus } from '@alpha/payments-dtos';
import { Box, Button, Tooltip } from '@alpha/ui-lib/ui/external';
import { Status } from '@alpha/ui-lib/ui/Status';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ActionDropDown from '../../FxTradeTable/DisplayTable/ActionDropDown/ActionDropDown';
import NoOfApprovers from '../../FxTradeTable/DisplayTable/NoOfApprovers/NoOfApprovers';
import TradeLink from '../../FxTradeTable/DisplayTable/TradeLink/TradeLink';

import useStyles from './PaymentTable.styles';

interface IPaymentTable {
  tableRawData: TSearchBaseResponse | undefined;
  hasNext: boolean;
  hasPrevious: boolean;
  loading: boolean;
  sortBy?: string;
  skip: number;
  sortOrder?: 'asc' | 'desc' | undefined,
  handleNextPage: (searchParams: TSearchParams, skipAmount?: number) => Promise<void>;
  handlePreviousPage: (searchParams: TSearchParams, skipAmount?: number) => Promise<void>;
  handleTableRowClick?: (e: PaymentDto) => void;
  handleTableSortClick?: (column: string) => void;
  emptyTitle?: string;
  emptySubTitle?: string;
  testId?: string,
  handleBeneficiaryButton?: (beneficiaryId: string) => void;
  selectedIds?: string[];
  setSelectedIds?: React.Dispatch<string[]>;
  setSelectedPayment?: React.Dispatch<React.SetStateAction<PaymentDto | undefined>>;
  handleReloadTable?: () => void;
  handleApprovePayments?: (payments: PaymentDto[]) => void;
  handleRejectPayments?: (payments: PaymentDto[]) => void;

}
const PaymentTable: React.FC<IPaymentTable> = (props: IPaymentTable) => {
  const {
    tableRawData,
    hasPrevious,
    hasNext,
    loading,
    sortOrder,
    sortBy,
    skip,
    handleTableRowClick,
    handleNextPage,
    handlePreviousPage,
    handleTableSortClick,
    emptyTitle,
    emptySubTitle,
    testId,
    handleBeneficiaryButton,
    setSelectedIds,
    selectedIds,
    setSelectedPayment,
    handleReloadTable,
    handleApprovePayments,
    handleRejectPayments,
  } = props;

  const classes = useStyles();
  const { authorized: canApproveOwn } = useAuthorization(
    [[UserRole.PAYMENTS_APPROVER_OWN]],
  );

  const [paymentNeedsVerification, setPaymentNeedsVerification] = useState<PaymentDto | undefined>(undefined);

  const userId = useSelector<TStore, string | undefined>((store) => store.user.profileDetails?.id);
  const canApproveOthers = useAuthorization([[UserRole.PAYMENTS_APPROVER]]);
  const [displayMultiSelect, setDisplayMultiSelect] = useState(false);
  const [currentOptions, setCurrentOptions] = useState<Approver[]>([]);
  const [textValue, setTextValue] = useState('');

  const handleBatchNumberClick = (e: PaymentDto) => {
    const paymentBatchId = e.batchId;
    if (paymentBatchId) {
      browserHistory.push(`/app/payments/${paymentBatchId}`);
    }
  };

  const displayColumns = () => {
    const commonColumns = [
      {
        header: t('payment_id'), accessor: 'contractNumber', sortable: true, width: 150,
      },
      {
        header: t('batch_id'), accessor: 'friendlyBatchId', sortable: true, width: 130,
      },
      {
        header: t('source'), accessor: 'origin', sortable: true, width: 120,
      },
      {
        header: t('beneficiary'), accessor: 'beneficiaryName', sortable: true, width: 180,
      },
      {
        header: t('value_date_IT_'), accessor: 'valueDate', sortable: true, width: 150,
      },
      {
        header: t('reference'), accessor: 'reference', sortable: true, width: 220,
      },
      {
        header: t('amount'), accessor: 'instructedAmount', sortable: true, width: 150,
      },
      { header: t('details'), accessor: 'details', width: 20 },
      {
        header: t('status'), accessor: 'paymentStatus', sortable: true, width: 200,
      },
      { header: t('actions'), accessor: 'actions', width: 60 },
    ];

    return [
      ...commonColumns,
    ];
  };

  const generateTableData = (tableData: TSearchBaseResponse | undefined): ITableData[] => (
    tableData?.records as PaymentDto[]
  )?.map((payment) => {
    const tradeContractNumber = payment.tradeContractNumber ?? 'TBC';

    const isDraftPayment = [
      PaymentStatus.VALIDATED,
      PaymentStatus.UPLOADED,
    ].includes(payment.status);
    const verificationRequired = payment.vopRequired
      && !payment.vopDetails?.vopApprovalAcknowledged
      && !isDraftPayment
      && payment.status !== PaymentStatus.CANCELLED;

    return {
      ...payment,
      actions: (
        <ActionDropDown
          payment={payment}
          setDisplayMultiSelect={setDisplayMultiSelect}
          displayMultiSelect={displayMultiSelect}
          setSelectedPayment={setSelectedPayment}
          handleReloadPaymentTable={handleReloadTable}
          handleApprovePayments={handleApprovePayments}
          handleRejectPayments={handleRejectPayments}
          currentOptions={currentOptions}
          setCurrentOptions={setCurrentOptions}
          setTextValue={setTextValue}
          pendingVerificationRow={verificationRequired}
        />
      ),
      contractNumber: (
        <Box
          display="flex"
          alignItems="left"
          justifyContent="flex-start"
          gridColumnGap="8px"
        >
          {verificationRequired && (
            <Tooltip title={t('vop_acknowledge_required')} classes={{ tooltip: classes.tooltip }} placement="bottom-start">
              <div style={{ marginTop: '2px', color: '#CF752F' }}>
                <FontAwesomeIcon icon={faExclamationCircle} />
              </div>
            </Tooltip>
          )}
          <Button
            disableRipple
            className={clsx(classes.tableButton, verificationRequired && classes.verificationRequiredCell)}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              if (setSelectedPayment) { setSelectedPayment(payment); }
            }}
          >
            {payment.contractNumber || '-'}
          </Button>
        </Box>
      ),
      friendlyBatchId: (
        <Button
          disableRipple
          className={clsx(classes.tableButton, verificationRequired && classes.verificationRequiredCell)}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (handleBatchNumberClick) { handleBatchNumberClick(payment); }
          }}
        >
          {payment.friendlyBatchId || '-'}
        </Button>),
      origin: <span className={clsx(verificationRequired && classes.verificationRequiredCell)}>{mapPaymentOrigin(payment.origin)}</span>,
      instructedAmount: (
        <span className={clsx(verificationRequired && classes.verificationRequiredCell)} style={{ fontWeight: 'bold' }}>
          {[payment.debitingCurrency, formatNumber(payment.amount, 2)].join(' ')}
        </span>
      ),
      valueDate: <span className={clsx(verificationRequired && classes.verificationRequiredCell)}>{formatIsoDate(payment.valueDate)}</span>,
      reference: <span className={clsx(verificationRequired && classes.verificationRequiredCell)}>{payment.reference}</span>,
      beneficiaryName: (
        <Button
          disableRipple
          className={clsx(classes.tableButton, verificationRequired && classes.verificationRequiredCell)}
          style={{ minWidth: '150px', margin: '0px 0px 0px 0px' }}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (handleBeneficiaryButton) { handleBeneficiaryButton(payment.beneficiaryId); }
          }}
        >
          <Box display="flex" alignItems="left" justifyContent="flex-start">
            {payment.vopRequired && (
            <VOPCheckTooltip
              vopCheck={payment.vopDetails?.status ?? PaymentVopMatchStatus.NOT_AVAILABLE}
              vopAccepted={payment.vopDetails?.vopApprovalAcknowledged}
            >
              <VOPStatusBadge
                status={payment.vopDetails?.status}
                onClick={(e) => {
                  e.stopPropagation();
                  setPaymentNeedsVerification(payment);
                }}
              />
            </VOPCheckTooltip>
            )}
            <span style={{ marginTop: '3px', marginLeft: '5px' }}>{payment.beneficiaryName || '-'}</span>
          </Box>
        </Button>),
      details: (
        payment
        && (
          <Box display="flex" alignItems="center" justifyContent="flex-start" gridGap="6px">
            <NoOfApprovers
              uploadedBy={payment.uploadedBy}
              uploadedDate={payment.uploadedDate}
              noOfApprovals={payment.approvalInfo.approvals?.length}
              approvedBy={payment.approvalInfo.approvals || []}
              approvalsRequired={payment.approvalInfo.approvalsRequired}
              verificationRequired={verificationRequired}
            />
            <TradeLink
              tradeContractNumber={tradeContractNumber}
              tradeSellCurrency={payment.tradeSellCurrency}
              tradeId={payment.tradeId ?? ''}
              handleTradeLinkClick={handleTradeLinkClick}
            />
          </Box>
        )
      ) || '-',
      paymentStatus: (
        <Status className={classes.status} variant={mapPaymentStatusDisplay(payment.status).variant}>
          {t(mapPaymentStatusDisplay(payment.status).text) || '-'}
        </Status>),
      paymentStatusStr: payment.status,
      paymentDto: payment,
    };
  });

  const handleTradeLinkClick = (tradeId: string) => {
    browserHistory.push(`/app/trades/${tradeId}`);
  };

  const compareTypeFunction = (
    firstItem: any,
    item: any,
  ): boolean => {
    if (firstItem.paymentStatusStr === item.paymentStatusStr) {
      if (item.paymentStatusStr === PaymentStatus.SUBMITTED) {
        const canApproveFirstItem = shouldShowApprovalButtons(
          canApproveOthers.authorized,
          canApproveOwn,
          [firstItem.paymentDto as PaymentDto],
          userId!,
          true,
        );
        const canApproveSelectedItem = shouldShowApprovalButtons(
          canApproveOthers.authorized,
          canApproveOwn,
          [item.paymentDto as PaymentDto],
          userId!,
          true,
        );
        return canApproveFirstItem === canApproveSelectedItem;
      }
      return true;
    }
    return false;
  };

  return (
    <>
      <SearchTable
        table={{
          columns: displayColumns() as ITableColumn[],
          data: generateTableData(tableRawData),
          clickable: true,
          handleTableRowClick,
          handleTableSortClick,
          activeColumn: sortBy,
          sortOrder,
          dataTestId: testId || '',
          multiSelectable: true,
          keyColumn: 'id',
          totalRowNumber: tableRawData?.total,
          canOnlySelectSameTypeBaseOnFunction: compareTypeFunction,
          checkboxDisabledMessage: t('you_can_only_select_payments_with_same_status'),
          setSelectedIds,
          selectedIds,
          skip,
        }}
        loading={loading}
        pagination={{
          handleNext: handleNextPage as any,
          handlePrevious: handlePreviousPage as any,
          hasNext,
          hasPrevious,
        }}
        emptyTable={{
          title: emptyTitle || t('no_payments'),
          subtitle: emptySubTitle || t('you_currently_do_not_have_any_payments'),
        }}
      />
      <VOPConfirmationModal
        readOnly
        open={!!paymentNeedsVerification}
        handleClose={() => setPaymentNeedsVerification(undefined)}
        payment={paymentNeedsVerification}
      />
    </>
  );
};
export default PaymentTable;
